import { BuildingOfficeIcon, PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import type { SiteListDto } from '@/api/generated/schemas';
import {
  EmptyState,
  ErrorState,
  LoadingSpinner,
  PageHeader,
  PaginationControls,
  SearchInput,
} from '@/components/common';
import { CreateSiteListModal, SiteListsTable } from '@/components/site-lists';
import { usePaginatedSearch } from '@/hooks/usePaginatedSearch';
import { useSearchSiteLists } from '@/queries/search.queries';
import { useSiteLists } from '@/queries/site-lists.queries';

export const Route = createFileRoute('/site-lists/')({
  component: SiteListsIndex,
});

function SiteListsIndex() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const pagination = usePaginatedSearch<SiteListDto>();
  const paginatedQuery = useSiteLists({ cursor: pagination.cursor, pageSize: pagination.pageSize });
  const searchQuery = useSearchSiteLists(pagination.debouncedSearch, pagination.searchPage);
  const {
    data: siteLists,
    isLoading,
    error,
    hasNextPage,
    hasPreviousPage,
    handleNextPage,
    handlePreviousPage,
    isFetching,
  } = pagination.withQueries({ paginatedQuery, searchQuery });

  const handleCreateSiteList = () => {
    setIsCreateModalOpen(false);
  };

  return (
    <div className="py-6">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <PageHeader
          title="Site Lists"
          description="Manage collections of sites for bulk operations and analysis"
          action={{
            label: 'New Site List',
            icon: PlusIcon,
            onPress: () => setIsCreateModalOpen(true),
            color: 'primary',
          }}
        />

        {/* Search Bar */}
        <div className="w-full sm:max-w-md">
          <SearchInput
            value={pagination.searchQuery}
            onChange={pagination.handleSearchChange}
            placeholder="Search site lists..."
          />
        </div>

        {/* Loading State */}
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorState title="Failed to load site lists" message="Please try again later" />
        ) : siteLists.length > 0 ? (
          <>
            <SiteListsTable siteLists={siteLists} />
            <PaginationControls
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onNextPage={handleNextPage}
              onPreviousPage={handlePreviousPage}
              isLoading={isFetching}
            />
          </>
        ) : (
          <EmptyState
            icon={BuildingOfficeIcon}
            title={pagination.searchQuery ? 'No site lists found matching your search' : 'No site lists created yet'}
            description="Create your first site list to group related sites together"
            variant={pagination.searchQuery ? 'search' : 'default'}
            action={
              pagination.searchQuery
                ? undefined
                : {
                    label: 'Create Site List',
                    icon: PlusIcon,
                    onPress: () => setIsCreateModalOpen(true),
                    color: 'primary',
                  }
            }
          />
        )}
      </div>

      <CreateSiteListModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateSiteList={handleCreateSiteList}
      />
    </div>
  );
}
